import type { ThreadActionSelection } from "@afar-research/contracts";

import { formatBrainActionPrompt } from "./brainActions.ts";
import type { BrainComposerCollection } from "./brainComposerCollection.ts";
import {
  brainAwareComposerCanSend,
  EMPTY_BRAIN_COMPOSER_COLLECTION,
} from "./brainComposerCollection.ts";
import { formatDesignOperationPrompt } from "./designActions.ts";
import type { DesignAgentOperationSubmission } from "./designActions.ts";
import { splitLeadingComposerSkillLines, withComposerSkillTokens } from "./composerSkillToken.ts";
import { brainActionOf, resolveThreadAction, threadActionSkillNames } from "./threadActions.ts";

/**
 * Lead a request with an Action's skill chips, keeping any chips the user
 * already typed after them.
 */
function withActionSkillTokens(skillNames: ReadonlyArray<string>, request: string): string {
  const { skillNames: typed, body } = splitLeadingComposerSkillLines(request);
  const invocations = [...new Set([...skillNames, ...typed])];
  return withComposerSkillTokens(invocations, body.length > 0 ? body : [""]);
}

/**
 * Everything that shapes the text of an outgoing send, in one place: a Design
 * Operation's arranged prompt, a Brain Action's, and every other Action's
 * skill chips in front of what the user wrote.
 *
 * An arranged prompt is built *here* and never by writing it into the composer
 * on the way to a send: the composer holds what the user typed, and a send that
 * borrowed it to carry the arranged prompt repainted the composer with text
 * nobody typed.
 */
export function formatOutgoingPrompt(input: {
  readonly text: string;
  /** The Action the composer holds; null for an ordinary message. */
  readonly action: ThreadActionSelection | null | undefined;
  /**
   * What a Brain Action's chips gathered. Defaulted rather than required: only
   * Ingest and Delete gather anything.
   */
  readonly brainCollection?: BrainComposerCollection | undefined;
  /** The agent-backed Design Operation this send carries, when it carries one. */
  readonly designOperation?: DesignAgentOperationSubmission | undefined;
}): string {
  if (input.designOperation !== undefined) {
    return formatDesignOperationPrompt(input.designOperation);
  }
  const trimmed = input.text.trim();
  const brainAction = brainActionOf(input.action);
  if (brainAction !== null) {
    // An Action that cannot send never earns its tokens: an Ingest or Delete
    // whose chips are empty is not a send.
    const brainCollection = input.brainCollection ?? EMPTY_BRAIN_COMPOSER_COLLECTION;
    return brainAwareComposerCanSend({
      action: brainAction,
      collection: brainCollection,
      hasComposerContent: trimmed.length > 0,
    })
      ? formatBrainActionPrompt({ action: brainAction, collection: brainCollection, text: trimmed })
      : trimmed;
  }
  const action = resolveThreadAction(input.action);
  if (action === null) return trimmed;
  const skills = threadActionSkillNames(action);
  return skills.length === 0 ? trimmed : withActionSkillTokens(skills, trimmed);
}
